import { useState } from "react";
import { Icon } from "../common/Icon";

/**
 * Back-panel for 1:1 ChatPortal — Agent personal settings
 * Goal tracking, work preferences, feedback to agent
 */
export function AgentSettingsPanel({ agentName }: { agentName: string }) {
  const [goalText, setGoalText] = useState("完成 60 篇多智能体协作领域的文献综述");
  const [goalProgress, setGoalProgress] = useState(78);
  const [commStyle, setCommStyle] = useState<0 | 1>(0);
  const [pace, setPace] = useState<0 | 1>(1);
  const [decision, setDecision] = useState<0 | 1>(0);
  const [reporting, setReporting] = useState<0 | 1>(1);
  const [feedbackText, setFeedbackText] = useState("");

  const PREF_ROWS: { label: string; options: [string, string]; value: 0 | 1; set: (v: 0 | 1) => void }[] = [
    { label: "沟通风格", options: ["简洁直接", "详细解释"], value: commStyle, set: setCommStyle },
    { label: "工作节奏", options: ["快速迭代", "深思熟虑"], value: pace, set: setPace },
    { label: "决策方式", options: ["数据驱动", "直觉判断"], value: decision, set: setDecision },
    { label: "汇报频率", options: ["实时更新", "完成后汇报"], value: reporting, set: setReporting },
  ];

  return (
    <div className="px-5 py-5 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <img
          src={`https://api.dicebear.com/7.x/bottts/svg?seed=${agentName}`}
          alt=""
          className="w-10 h-10 rounded bg-white/[0.04]"
        />
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-white">{agentName}</p>
          <p className="text-[11px] text-gray-400">个人设置</p>
        </div>
      </div>

      {/* Goal */}
      <section>
        <SectionHeader icon="flag" title="当前目标" />
        <textarea
          value={goalText}
          onChange={(e) => setGoalText(e.target.value)}
          rows={2}
          className="w-full px-3.5 py-2.5 rounded bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-sm text-gray-800 dark:text-gray-200 outline-none focus:border-emerald-500/40 resize-none transition-colors"
        />
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-400">进度</span>
            <span className="text-xs font-medium text-emerald-400">{goalProgress}%</span>
          </div>
          <input
            type="range" min={0} max={100} value={goalProgress}
            onChange={(e) => setGoalProgress(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer bg-gray-100 dark:bg-gray-800 accent-emerald-500"
          />
        </div>
      </section>

      {/* Work prefs */}
      <section>
        <SectionHeader icon="tune" title="工作偏好" />
        <div className="space-y-3">
          {PREF_ROWS.map((row) => (
            <div key={row.label}>
              <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1.5">{row.label}</p>
              <div className="flex rounded overflow-hidden border border-gray-200 dark:border-white/[0.06]">
                {row.options.map((opt, oi) => (
                  <button
                    key={opt}
                    onClick={() => row.set(oi as 0 | 1)}
                    className={`flex-1 py-2.5 text-[13px] font-medium transition-all ${
                      row.value === oi ? "bg-emerald-500/10 text-emerald-400" : "bg-transparent text-gray-500 hover:text-gray-400"
                    } ${oi === 0 ? "border-r border-gray-200 dark:border-white/[0.06]" : ""}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Feedback */}
      <section>
        <SectionHeader icon="rate_review" title="反馈纠偏" />
        <p className="text-[12px] text-gray-400 mb-2">告诉 {agentName} 哪里做得好、哪里需要改，它会学习并调整</p>
        <textarea
          value={feedbackText}
          onChange={(e) => setFeedbackText(e.target.value)}
          rows={3}
          placeholder="例如：下次分析论文时，多关注方法论的局限性..."
          className="w-full px-3.5 py-2.5 rounded bg-gray-50 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] text-sm text-gray-800 dark:text-gray-200 placeholder:text-gray-400 outline-none focus:border-emerald-500/40 resize-none transition-colors"
        />
        {feedbackText.trim() && (
          <button className="mt-2 px-4 py-2 rounded text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-500 transition-colors">
            提交反馈
          </button>
        )}
      </section>
    </div>
  );
}

function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon name={icon} size={18} className="text-emerald-500" />
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
    </div>
  );
}
